/**
 * AI Form Registry - Central registry of all available form metadata
 *
 * Maps form keys to AiFormMetadata constants exported from their respective components.
 * This allows the AI assistant to dynamically discover and fill any registered form
 * without needing to import the metadata constants directly.
 */

import { AiFormMetadata } from '@/scripts/userflow';
import { ROLE_FORM_METADATA } from '@/components/roles/RoleFormModal';
import { INVITE_USER_FORM_METADATA } from '@/components/users/InviteUserModal';

const formRegistry: Record<string, AiFormMetadata> = {
  create_role_form: ROLE_FORM_METADATA,
  invite_user_form: INVITE_USER_FORM_METADATA,
};

/**
 * Get form metadata by key
 * @param metadataKey - The form key (e.g., 'create_role_form')
 * @returns AiFormMetadata or null if not found
 */
export function getAiFormMetadata(metadataKey: string): AiFormMetadata | null {
  return formRegistry[metadataKey] ?? null;
}

/**
 * Register a new form metadata
 * @param key - Form key
 * @param metadata - Form metadata object
 */
export function registerAiForm(key: string, metadata: AiFormMetadata): void {
  formRegistry[key] = metadata;
}

/**
 * Get all registered forms
 */
export function getAllAiFormMetadata(): Record<string, AiFormMetadata> {
  return { ...formRegistry };
}

/**
 * Check if a form is registered
 */
export function hasAiForm(metadataKey: string): boolean {
  return metadataKey in formRegistry;
}
